// The exact field set FallbackToLLMConfirmationStrategy.consolidate_llm_statuses returns:
// QUORUM_FIELDS followed by the three list fields it votes on separately. Keeping this list
// in the same order the backend defines it means the diff table reads the same way as the
// quorum logic, and -- because every downstream confirm/question/reject decision reads only
// from this dict -- "a field here changed" and "the outcome could change" are the same claim.
//
// Do not add display-only fields to this list. Widening it breaks that equivalence and the
// change count stops meaning anything.
export const CONSOLIDATED_FIELDS = [
  { key: "status", label: "status" },
  { key: "appointment_status", label: "appointment_status" },
  { key: "confidence_level", label: "confidence_level" },
  { key: "conf_date_tm_from", label: "conf_date_tm_from" },
  { key: "conf_date_tm_to", label: "conf_date_tm_to" },
  { key: "confirmation_code", label: "confirmation_code" },
  { key: "appt_num", label: "appt_num" },
  { key: "requesting_confirmation", label: "requesting_confirmation" },
  { key: "confirmed_but_has_question_or_info", label: "confirmed_but_has_question_or_info" },
  { key: "check_back_later", label: "check_back_later" },
  { key: "check_back_datetime", label: "check_back_datetime" },
  { key: "requested_load_type", label: "requested_load_type" },
  { key: "confirmed_load_type", label: "confirmed_load_type" },
  { key: "is_automated_message", label: "is_automated_message" },
  { key: "suggested_times", label: "suggested_times" },
  { key: "suggested_ranges", label: "suggested_ranges" },
  { key: "reference_numbers", label: "reference_numbers" },
]

// Where the effective instruction resolved from in get_ten_loc_cfg's overlay. Anything other
// than TENANT means saving creates an override rather than editing what is displayed.
export const SOURCE_LABELS = {
  TENANT: "tenant override",
  LOCATION: "inherited from location default",
  APPT_TYPE: "inherited from EMAIL default",
  BASE: "inherited from base default",
  NONE: "no instruction set",
}

export const SOURCE_COLORS = {
  TENANT: "success",
  LOCATION: "warning",
  APPT_TYPE: "warning",
  BASE: "warning",
  NONE: "secondary",
}

// A group reply's consolidated status has a different shape -- the per-appointment decision
// lives inside appointments[], not in flat conf_date_tm_* fields. These mirror
// GroupApptConfirmationStrategy._consolidate_llm_statuses, and the server's changed_fields
// names them "appt[<row_index>].<field>".
export const GROUP_TOP_FIELDS = [
  { key: "appointment_status", label: "appointment_status" },
  { key: "overall_response_type", label: "overall_response_type" },
  { key: "appointments_confirmed", label: "appointments_confirmed" },
  { key: "total_appointments_requested", label: "total_appointments_requested" },
  { key: "confirmed_but_has_question_or_info", label: "confirmed_but_has_question_or_info" },
  { key: "is_automated_message", label: "is_automated_message" },
]

export const GROUP_APPT_FIELDS = [
  { key: "status", label: "status" },
  { key: "confidence_level", label: "confidence_level" },
  { key: "confirmation_code", label: "confirmation_code" },
  { key: "confirmed_date_from", label: "confirmed_date_from" },
  { key: "confirmed_date_to", label: "confirmed_date_to" },
  { key: "responder_reference", label: "responder_reference" },
  { key: "requesting_confirmation", label: "requesting_confirmation" },
  { key: "requested_load_type", label: "requested_load_type" },
  { key: "confirmed_load_type", label: "confirmed_load_type" },
  { key: "check_back_later", label: "check_back_later" },
  { key: "check_back_datetime", label: "check_back_datetime" },
  { key: "suggested_times", label: "suggested_times" },
  { key: "reference_numbers", label: "reference_numbers" },
]

// Shown on a preview row so a group result is never mistaken for a single-appointment one.
// Spelled out rather than abbreviated: a bare "single" beside a reply reads as a property of
// the reply, not as the name of the strategy that was replayed over it.
export const STRATEGY_LABELS = {
  FallbackToLLMConfirmationStrategy: "single appointment",
  GroupApptConfirmationStrategy: "group",
  GroupingKeyGroupApptConfirmationStrategy: "grouping key",
  SpreadsheetConfirmationStrategy: "spreadsheet",
  SpreadsheetRefMultiConfirmationStrategy: "spreadsheet (ref-multi)",
  CancellationConfirmationStrategy: "cancellation",
  SysOriginatedLLMStrategy: "sys-originated",
}

// How the backend established which strategy to replay under. Worth showing beside the diff:
// "default" means nothing was recorded for this reply and the single-appointment strategy was
// assumed, which is the one case where the Before and After may not be like for like.
export const STRATEGY_SOURCE_NOTES = {
  recorded_name: "as recorded with this reply",
  sys_originated_loc: "from this location's sys-originated flow",
  default: "assumed, because nothing was recorded with this reply",
}

export const MAX_INSTR_CHARS = 2000

// appt_emailrsp_manual_review stops auto-appending once addnl_llm_instr_cnt reaches this.
export const AUTO_APPEND_CAP = 3

// The two validation runs the page offers. "Validate for QD Ref" replays every stored reply
// under the tracker that was pasted; "Validate for last 10" replays the location's recent
// history, which is where collateral damage shows up. Both are fixed actions -- the window
// and the count below are stated, not configurable, because a dropdown invites tuning the
// sample instead of the instruction.
export const SCOPE_QD_REF = "qd_ref"
export const SCOPE_RECENT = "recent"

export const DEFAULT_SAMPLE_DAYS = 30
export const DEFAULT_SAMPLE_SIZE = 10
